import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { getResolvedClosedByResponsibleOverTime } from '../../services/ticketService';
import ResponsiblePerformanceOverTimeChart from '../../components/dashboards/ResponsiblePerformanceOverTimeChart';
import DashboardDemandTypeFilter from '../../components/dashboards/DashboardDemandTypeFilter';
import DashboardPeriodFilter from '../../components/dashboards/DashboardPeriodFilter';
import DashboardTimeBucketFilter from '../../components/dashboards/DashboardTimeBucketFilter';
import FieldFilter from '../../components/shared/FieldFilter';
import CollapsibleFilters from '../../components/shared/CollapsibleFilters';
import { useApp } from '../../context/AppContext';
import { buildPeriodApiParams } from '../../services/dashboardPeriodService';
import { DEFAULT_TIME_BUCKET } from '../../services/dashboardTimeBucketService';

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 2rem;
  background-color: var(--color-bg-secondary);
  min-height: 100vh;
  overflow-x: hidden;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const PageTitle = styled.h1`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 650;
  letter-spacing: -0.02em;
  line-height: 1.2;
  color: var(--color-text-primary);
`;

const PageDescription = styled.p`
  margin: 0;
  color: var(--color-text-secondary);
  font-size: 0.9rem;
  line-height: 1.5;
  max-width: 720px;
`;

const FiltersRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: flex-start;
  width: 100%;

  > * {
    margin-bottom: 0;
    align-self: flex-start;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;

    > * {
      width: 100%;
    }
  }
`;

const TREND_DEFAULT_PERIOD = 'THIS_YEAR';

const ResponsiblePerformanceOverTimePage = () => {
  const { users } = useApp();
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [filterDemandType, setFilterDemandType] = useState([]);
  const [periodPreset, setPeriodPreset] = useState(TREND_DEFAULT_PERIOD);
  const [timeBucket, setTimeBucket] = useState(DEFAULT_TIME_BUCKET);
  const [selectedResponsibles, setSelectedResponsibles] = useState([]);

  const periodParams = useMemo(() => {
    const preset = periodPreset === 'ALL' ? TREND_DEFAULT_PERIOD : periodPreset;
    return buildPeriodApiParams(preset);
  }, [periodPreset]);

  const responsibleFilterOptions = useMemo(() => {
    const names = new Set();
    (Array.isArray(users) ? users : []).forEach((user) => {
      const name = user?.name?.trim();
      if (name) names.add(name);
    });
    return [...names]
      .sort((a, b) => a.localeCompare(b, 'pt-BR'))
      .map((name) => ({ value: name, label: name }));
  }, [users]);

  const seriesOrder = useMemo(() => {
    if (selectedResponsibles.length > 0) return [...selectedResponsibles];
    const names = new Set();
    (Array.isArray(content) ? content : []).forEach((row) => {
      if (row?.responsibleUserName) names.add(String(row.responsibleUserName));
    });
    return [...names];
  }, [selectedResponsibles, content]);

  const fetchData = useCallback(async () => {
    if (!periodParams.from || !periodParams.to) return;
    setLoading(true);
    setError(false);
    try {
      const res = await getResolvedClosedByResponsibleOverTime({
        from: periodParams.from,
        to: periodParams.to,
        bucket: timeBucket,
        demandType: filterDemandType,
        responsibleUserName: selectedResponsibles,
      });
      const rows = res?.content ?? res;
      setContent(Array.isArray(rows) ? rows : []);
    } catch (err) {
      console.error('Error fetching responsible performance over time:', err);
      setContent([]);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [
    periodParams.from,
    periodParams.to,
    timeBucket,
    filterDemandType,
    selectedResponsibles,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (periodPreset && periodPreset !== TREND_DEFAULT_PERIOD) n += 1;
    if (timeBucket && timeBucket !== DEFAULT_TIME_BUCKET) n += 1;
    if (Array.isArray(filterDemandType) && filterDemandType.length > 0) n += 1;
    if (Array.isArray(selectedResponsibles) && selectedResponsibles.length > 0) n += 1;
    return n;
  }, [periodPreset, timeBucket, filterDemandType, selectedResponsibles]);

  return (
    <PageContainer>
      <PageTitle>Dashboards • Evolução por responsável</PageTitle>
      <PageDescription>
        Acompanhe a performance de cada recurso ao longo do tempo — quantos tickets foram
        resolvidos ou fechados por semana, mês ou trimestre.
      </PageDescription>

      <CollapsibleFilters activeCount={activeFilterCount}>
        <FiltersRow>
          <DashboardPeriodFilter
            value={periodPreset}
            onChange={setPeriodPreset}
            disabled={loading}
            fieldId="dashboard-responsible-trend-period"
          />
          <DashboardTimeBucketFilter
            value={timeBucket}
            onChange={setTimeBucket}
            disabled={loading}
            fieldId="dashboard-responsible-trend-bucket"
          />
          <DashboardDemandTypeFilter
            selectedValues={filterDemandType}
            onValuesChange={setFilterDemandType}
            disabled={loading}
            fieldId="dashboard-responsible-trend-demand-type"
          />
          {responsibleFilterOptions.length > 0 && (
            <FieldFilter
              label="Responsável:"
              fieldId="dashboard-responsible-trend-responsible"
              selectedValues={selectedResponsibles}
              onValuesChange={setSelectedResponsibles}
              items={responsibleFilterOptions}
              disabled={loading}
              inlineToolbar
            />
          )}
        </FiltersRow>
      </CollapsibleFilters>

      <ResponsiblePerformanceOverTimeChart
        content={content}
        seriesOrder={seriesOrder}
        loading={loading}
        error={error}
        onRetry={fetchData}
        periodLabel={periodParams.label}
        timeBucket={timeBucket}
        showTopResponsiblesHint={selectedResponsibles.length === 0}
      />
    </PageContainer>
  );
};

export default ResponsiblePerformanceOverTimePage;
